import React, { useState, useEffect } from 'react'
import {
  Container,
  DropdownsContainer,
  FixedContainer,
  SelectionContainer,
  LoadingContainer,
  NoResults,
  LoadMoreButton,
  LoadMoreContainer,
  Wrapper
} from './ModelSelection.styles'
import Search from '../Search/Search'
import CustomDropdown from '../Dropdown/Dropdown'
import ModelSelectionButton from '../ModelSelectionButton/ModelSelectionButton'
import { Product } from '../../services/types/product'
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner'
import {
  useGetCategories,
  useGetMaterials,
  useSearchProducts
} from '../../hooks/api'
import Toast, { ToastIntentEnum } from '../Toast/Toast'

type ModelSelectionProps = {
  initialData: {
    products: Product[]
    nextSkipToken: string
  }
  setSkipToken: (skipToken: string) => void
  brandId: string
  isLoading: boolean
  selectedProducts: Product[]
  setSelectedProducts: (products: Product[]) => void
  isFetching?: boolean
  isPlaceholderData?: boolean
}

type SelectedOption = {
  id: string
  name: string
} | null

const ModelSelection: React.FC<ModelSelectionProps> = ({
  brandId,
  isLoading,
  selectedProducts,
  setSelectedProducts,
  initialData,
  setSkipToken,
  isPlaceholderData,
  isFetching
}) => {
  const MIN_SEARCH_TERM_LENGTH = 2
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [selectedCategory, setSelectedCategory] = useState<SelectedOption>(null)
  const [selectedMaterial, setSelectedMaterial] = useState<SelectedOption>(null)

  const [products, setProducts] = useState<Product[]>(initialData.products)

  const isSearching =
    searchTerm.length > MIN_SEARCH_TERM_LENGTH ||
    !!selectedCategory ||
    !!selectedMaterial

  const {
    data: searchResults = [],
    isLoading: isSearchingLoading,
    error
  } = useSearchProducts({
    query: isSearching ? searchTerm : null,
    brandId: brandId,
    categoryId: selectedCategory?.id || null,
    materialId: selectedMaterial?.id || null,
    enabled: isSearching
  })

  const { data: categories = [], error: categoriesError } = useGetCategories()
  const { data: materials = [], error: materialsError } = useGetMaterials()

  const handleCategorySelect = (option: SelectedOption) => {
    setSelectedCategory(option)
  }

  const handleMaterialsSelect = (option: SelectedOption) => {
    setSelectedMaterial(option)
  }

  const onLoadMoreClick = () => {
    setSkipToken(initialData.nextSkipToken)
  }

  const categoryOptions = categories.map((category) => ({
    id: category.id || category.name,
    name: category.name
  }))

  const materialOptions = materials.map((material) => ({
    id: material.id || material.name,
    name: material.name
  }))

  const searchResultsCount = searchResults.length

  useEffect(() => {
    if (initialData.products) {
      setProducts((prevProducts) => {
        const existingProductIds = new Set(
          prevProducts.map((product) => product.id)
        )
        const newProducts = initialData.products.filter(
          (product) => !existingProductIds.has(product.id)
        )
        return [...prevProducts, ...newProducts]
      })
    }
  }, [initialData.products])

  if (error || categoriesError || materialsError) {
    return (
      <Toast
        triggerToast={ToastIntentEnum.Error}
        title={error?.message || 'There was an error getting the data'}
      />
    )
  }

  return (
    <Container>
      <div style={{ width: '100%' }}>
        <FixedContainer>
          <Search searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          <DropdownsContainer>
            <CustomDropdown
              label="Category"
              customPlaceholder="Select a category"
              options={categoryOptions}
              selectedOption={selectedCategory}
              onOptionChange={handleCategorySelect}
            />
            <CustomDropdown
              label="Material"
              customPlaceholder="Select a material"
              options={materialOptions}
              selectedOption={selectedMaterial}
              onOptionChange={handleMaterialsSelect}
            />
          </DropdownsContainer>
          {searchResultsCount > 0 && isSearching && (
            <>
              {searchResultsCount === 200 ? (
                <p style={{ marginBottom: '10px' }}>
                  More than 200 products found
                </p>
              ) : (
                <p style={{ marginBottom: '10px' }}>
                  <strong>{searchResultsCount}</strong> product(s) found
                </p>
              )}
            </>
          )}
        </FixedContainer>
        {isLoading || isSearchingLoading ? (
          <LoadingContainer>
            <LoadingSpinner height="60" />
          </LoadingContainer>
        ) : (
          <Wrapper>
            <SelectionContainer>
              {isSearching ? (
                searchResults.length > 0 ? (
                  searchResults.map((product: Product) => (
                    <ModelSelectionButton
                      key={product.id}
                      product={product}
                      selectedProducts={selectedProducts}
                      setSelectedProducts={setSelectedProducts}
                    />
                  ))
                ) : (
                  <>
                    {!isSearchingLoading && (
                      <NoResults>
                        No products matched your search and / or filters
                        criteria.
                      </NoResults>
                    )}
                  </>
                )
              ) : products.length > 0 ? (
                products.map((product: Product) => (
                  <ModelSelectionButton
                    key={product.id}
                    product={product}
                    selectedProducts={selectedProducts}
                    setSelectedProducts={setSelectedProducts}
                  />
                ))
              ) : (
                <h3>No products available for this brand.</h3>
              )}
              {!isSearching && initialData?.nextSkipToken && (
                <LoadMoreContainer>
                  {isFetching ? (
                    <p>Loading..</p>
                  ) : (
                    <LoadMoreButton
                      onClick={onLoadMoreClick}
                      disabled={isFetching || isPlaceholderData}
                    >
                      Load More
                    </LoadMoreButton>
                  )}
                </LoadMoreContainer>
              )}
            </SelectionContainer>
          </Wrapper>
        )}
      </div>
    </Container>
  )
}

export default ModelSelection
